import { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, Wallet, ArrowRight } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext.js';
import { DashboardLayout, PageHeader, StatCard, AmountCard } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Button from '../common/Button.js';
import userService from '../../services/UserService.js';
import mlAgentCommissionService from '../../services/mlAgentCommissionService.js';
import RefreshingBanner from './components/RefreshingBanner.js';
import { dashboardCacheKey, readDashboardCache, writeDashboardCache } from '../../utils/dashboardCache.js';

/**
 * Contract Party Admin home — party-scoped agents, ML sheets, commission payments.
 */
const ContractPartyAdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const initialRef = useRef(null);
  if (initialRef.current == null) {
    const cacheKey = dashboardCacheKey('contract-party', user);
    initialRef.current = { cacheKey, cached: readDashboardCache(cacheKey) };
  }
  const { cacheKey, cached } = initialRef.current;
  const [loading, setLoading] = useState(!cached);
  const [refreshing, setRefreshing] = useState(Boolean(cached));
  const [agentCount, setAgentCount] = useState(cached?.agentCount || 0);
  const [commission, setCommission] = useState(cached?.commission || {
    total_amount: 0,
    paid_amount: 0,
    unpaid_amount: 0,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!cached) setLoading(true);
        else setRefreshing(true);
        const [usersRes, summaryRes] = await Promise.all([
          userService.getAll(null, null, { all: true }).catch(() => null),
          mlAgentCommissionService.getSummary().catch(() => null),
        ]);
        if (cancelled) return;

        const rawUsers = usersRes?.data ?? usersRes?.users ?? usersRes;
        const list = Array.isArray(rawUsers)
          ? rawUsers
          : Array.isArray(rawUsers?.data)
            ? rawUsers.data
            : [];
        const agents = list.filter((u) => {
          const role = u.role_name || u.role;
          return role === 'Agent';
        });
        const nextCommission = {
          total_amount: Number((summaryRes?.data || {}).total_amount || 0),
          paid_amount: Number((summaryRes?.data || {}).paid_amount || 0),
          unpaid_amount: Number((summaryRes?.data || {}).unpaid_amount || 0),
        };
        setAgentCount(agents.length);
        setCommission(nextCommission);
        writeDashboardCache(cacheKey, { agentCount: agents.length, commission: nextCommission });
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user?.user_id, user?.company_id]);

  const partyName = user?.party_name || user?.contract_party_name || 'Your contract party';

  if (loading) {
    return (
      <DashboardLayout>
        <Loader fullScreen={false} message="Loading dashboard..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Contract Party Dashboard"
        subtitle={`${partyName} — agents, ML sheets, and commission payments for your party only`}
      />
      <RefreshingBanner show={refreshing} />

      <div className={`grid grid-cols-1 ${Number(commission.total_amount) > 0.009 ? 'sm:grid-cols-2' : ''} gap-4 mb-6`}>
        <button type="button" className="text-left" onClick={() => navigate('/users')}>
          <StatCard
            label="Party Agents"
            value={agentCount}
            icon={<Users className="w-5 h-5 text-white" />}
            color="bg-blue-600"
          />
        </button>
        {Number(commission.total_amount) > 0.009 && (
        <button type="button" className="text-left" onClick={() => navigate('/ml-commission-payments')}>
          <AmountCard
            label="Agent Commission"
            amount={commission.total_amount}
            icon={<Wallet className="w-5 h-5 text-white" />}
            color="bg-teal-600"
            subAmount={commission.paid_amount}
            subLabel="Paid"
            subAmount2={commission.unpaid_amount}
            subLabel2="Unpaid"
          />
        </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Quick links</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="secondary" icon={Users} onClick={() => navigate('/users')}>
            Manage Party Agents
          </Button>
          <Button variant="secondary" icon={FileText} onClick={() => navigate('/ml-sheets')}>
            Dispatch
          </Button>
          <Button variant="primary" icon={Wallet} onClick={() => navigate('/ml-commission-payments')}>
            Book & Delivery Comm.
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ContractPartyAdminDashboard;
